import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect } from "react";

export const CreatePinDialog = ({
  open,
  onClose,
  onConfirm,
  existingPinNames,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (pinName: string, setAsDefault: boolean) => void;
  existingPinNames: string[];
}) => {
  const [pinName, setPinName] = useState("");
  const [error, setError] = useState("");
  const [setAsDefault, setSetAsDefault] = useState(false);

  useEffect(() => {
    if (open) {
      setPinName("");
      setError("");
      setSetAsDefault(false);
    }
  }, [open]);

  const validatePinName = (nameToValidate?: string): string | null => {
    const name = nameToValidate ?? pinName;
    const trimmedName = name.trim();

    if (!trimmedName) {
      setError("Pin name is required");
      return null;
    }

    // Check for case-insensitive uniqueness
    const normalizedExisting = existingPinNames.map((n) =>
      n.toLowerCase().trim()
    );
    if (normalizedExisting.includes(trimmedName.toLowerCase())) {
      setError("A pin with this name already exists");
      return null;
    }

    if (trimmedName.length > 50) {
      setError("Pin name must be 50 characters or less");
      return null;
    }

    // Validation passed - clear any existing error
    setError("");
    return trimmedName;
  };

  const handleConfirm = () => {
    const trimmedName = validatePinName();
    if (!trimmedName) return;

    onConfirm(trimmedName, setAsDefault);
    setPinName("");
    setError("");
    setSetAsDefault(false);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleConfirm();
    }
  };

  const theme = useTheme();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>Create New Pin</Box>
        <IconButton
          onClick={onClose}
          sx={{
            p: 0,
            color: theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box>
          <Typography variant="body1" gutterBottom>
            Name this pin to save your current view settings:
          </Typography>
          <TextField
            autoFocus
            fullWidth
            value={pinName}
            variant="standard"
            sx={{ mt: -0.5 }}
            onChange={(e) => {
              const newValue = e.target.value;
              setPinName(newValue);
              // Validate on change to show errors in real-time
              if (newValue.trim()) {
                validatePinName(newValue);
              } else {
                setError("");
              }
              // Note: validatePinName will clear error if validation passes
            }}
            onKeyPress={handleKeyPress}
            error={!!error}
            helperText={error || "Enter a descriptive name for this pin"}
            margin="normal"
            placeholder="e.g., Q1 Analysis, Holiday Sales, Default View"
          />
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={setAsDefault}
                  onChange={(e) => setSetAsDefault(e.target.checked)}
                />
              }
              label="Set as default"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!pinName.trim() || !!error}
        >
          Create Pin
        </Button>
      </DialogActions>
    </Dialog>
  );
};
